import React, { useRef } from 'react';
import { styled } from 'styletron-react';
import { DocumentScanner } from './DocumentScanner';
import { UploadArea } from './UploadArea';
import { NationalityPicker } from './NationalityPicker';
import { useLang } from '../i18n/publicEvents';

export interface GuestInfo {
  name: string;
  last_name: string;
  document_id: string;
  dob: string;
  nationality: string;
  expiration_date: string;
  // S3 key of the cropped face photo returned by the OCR service.
  // Optional so callers that don't run OCR (e.g., manual entry) can omit it.
  photo_url?: string;
}

interface GuestScanFormProps {
  guest: GuestInfo;
  onChange: (field: keyof GuestInfo, value: string) => void;

  scanning: boolean;
  scanStatus: 'loading' | 'success' | 'error';
  scannedPreview: string;
  onScanFile: (file: File) => void;
  scanTitle: string;
  scanHint?: string;

  error?: string;
  tenantPrimaryColor?: string;
  autoFocusFirstField?: boolean;
}

const ScanCard = styled('div', {
  border: '2px dashed #D1D5DB',
  borderRadius: '12px',
  padding: '32px 16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  marginBottom: '20px',
  overflow: 'hidden',
  position: 'relative',
  ':hover': { borderColor: 'var(--tenant-primary, #000)', backgroundColor: '#FAFAFA' },
});

const ScanOverlay = styled('div', {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(255,255,255,0.92)',
  zIndex: 2000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
});

const DocPreview = styled('img', {
  width: '100%',
  objectFit: 'contain',
  borderRadius: '12px 12px 0 0',
});

const FilledInput = styled('input', {
  width: '100%', padding: '14px 14px', borderRadius: '8px', border: 'none',
  // 16px minimum to stop iOS Safari from auto-zooming on focus.
  fontSize: '16px', color: '#000', outline: 'none', boxSizing: 'border-box',
  backgroundColor: '#F3F3F3',
});

const FilledInputTitle = styled('div', {
  fontSize: '16px', fontWeight: 600, color: '#000', marginBottom: '10px',
});

const ErrorMsg = styled('div', {
  color: '#DC2626',
  fontSize: '14px',
  marginTop: '12px',
  textAlign: 'center',
});

export const GuestScanForm: React.FC<GuestScanFormProps> = ({
  guest,
  onChange,
  scanning,
  scanStatus,
  scannedPreview,
  onScanFile,
  scanTitle,
  scanHint,
  error,
  tenantPrimaryColor,
  autoFocusFirstField,
}) => {
  const { t } = useLang();
  const scanFileRef = useRef<HTMLInputElement>(null);

  // Gate the editable fields on a successful scan: until the user uploads an
  // ID and the OCR extracts at least a document number, we keep the form
  // hidden so people can't bypass identification by typing arbitrary names.
  // After the scan we show the fields pre-filled and editable so they can
  // correct OCR mistakes.
  const scanCompleted = !!(scannedPreview && guest.document_id);

  return (
    <>
      {scanning && (
        <ScanOverlay>
          <DocumentScanner
            status={scanStatus}
            size="md"
            color={tenantPrimaryColor}
            background="#fff"
            successLabel={t('documentAnalyzed')}
            errorLabel={t('couldNotAnalyzeDocument')}
          />
        </ScanOverlay>
      )}

      {scanCompleted ? (
        <ScanCard
          onClick={() => !scanning && scanFileRef.current?.click()}
          style={{ padding: '0', border: '1px solid #E8E8E8' }}
        >
          <input
            ref={scanFileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) onScanFile(file);
              e.target.value = '';
            }}
          />
          <DocPreview src={scannedPreview} alt="Scanned document" />
          <div style={{ fontSize: '13px', color: '#9CA3AF', padding: '8px 0' }}>{t('tapToScanAgain')}</div>
        </ScanCard>
      ) : (
        <>
          <UploadArea
            onFileSelect={onScanFile}
            dragEnabled={false}
            accept="image/*"
            title={scanTitle}
            hint={scanHint || t('takePhotoHint')}
            buttonLabel={t('upload')}
            color={tenantPrimaryColor}
            disabled={scanning}
          />
          <div style={{ fontSize: '13px', color: '#6B7280', textAlign: 'center', marginTop: '12px' }}>
            {t('weWillFill')}
          </div>
        </>
      )}

      {scanCompleted && (
        <>
          <div style={{ marginBottom: 20 }}>
            <FilledInputTitle>{t('identificationNumber')}</FilledInputTitle>
            <FilledInput
              value={guest.document_id}
              onChange={e => onChange('document_id', e.target.value)}
              placeholder="001-0000000-0"
              autoFocus={autoFocusFirstField}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <FilledInputTitle>{t('name')}</FilledInputTitle>
            <FilledInput value={guest.name} onChange={e => onChange('name', e.target.value)} placeholder="John" />
          </div>

          <div style={{ marginBottom: 20 }}>
            <FilledInputTitle>{t('lastName')}</FilledInputTitle>
            <FilledInput value={guest.last_name} onChange={e => onChange('last_name', e.target.value)} placeholder="Doe" />
          </div>

          <div style={{ marginBottom: 20 }}>
            <FilledInputTitle>{t('dateOfBirth')}</FilledInputTitle>
            <FilledInput value={guest.dob} onChange={e => onChange('dob', e.target.value)} placeholder="MM/DD/YY" />
          </div>

          <div style={{ marginBottom: 20 }}>
            <FilledInputTitle>{t('nationality')}</FilledInputTitle>
            <NationalityPicker
              value={guest.nationality}
              onChange={(code) => onChange('nationality', code)}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <FilledInputTitle>{t('expirationDate')}</FilledInputTitle>
            <FilledInput value={guest.expiration_date} onChange={e => onChange('expiration_date', e.target.value)} placeholder="MM/DD/YY" />
          </div>
        </>
      )}

      {error && <ErrorMsg>{error}</ErrorMsg>}
    </>
  );
};
